<template>
  <FilterGroup
    v-if="hasPriceRange"
    :label="filter.name"
    :show-action="hasValueChanged"
    :is-collapsible="isCollapsible"
    :is-active="hasActiveState"
    :filter-slug="filter.slug"
    @click:reset="handleReset"
    @click:apply="handleApply"
    @reset-to-initial-state="handleResetToInitialState"
  >
    <div class="mx-4 mb-3 flex items-center justify-between">
      <CurrencyInput
        class="w-5/12"
        :model-value="fromValue"
        :min="roundDownValue(minValue)"
        :max="toValue"
        :label="$t('price_range.min_value_label')"
        @update:model-value="handleFromInputChange"
      />
      <span>{{ $t('price_range.to') }}</span>
      <CurrencyInput
        :model-value="toValue"
        class="w-5/12"
        :min="fromValue"
        :max="roundUpValue(maxValue)"
        :label="$t('price_range.max-value_label')"
        @update:model-value="handleToInputChange"
      />
    </div>
    <RangeSlider
      :model-value="tempModelValue"
      :max="roundUpValue(maxValue) * 100"
      :min="roundDownValue(minValue) * 100"
      @update:model-value="handleRangeChange"
    />
  </FilterGroup>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import type { ProductFilter } from '@scayle/storefront-nuxt'
import type { RangeTuple } from '~/components/ui/sliders/RangeSlider.vue'
import FilterGroup from '~/components/filters/FilterGroup.vue'
import RangeSlider from '~/components/ui/sliders/RangeSlider.vue'
import CurrencyInput from '#layers/base/components/ui/forms/inputs/CurrencyInput.vue'
import { useDeviceType } from '~/composables/useDeviceType'

const INPUT_CHANGE_DELAY = 300

const props = defineProps<{
  modelValue: RangeTuple
  filter: ProductFilter
  minValue: number
  maxValue: number
  filterValues: any[]
  isCollapsible?: boolean
}>()
const emit = defineEmits<{
  reset: []
  apply: []
  'update:modelValue': [RangeTuple]
}>()

const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobileOrTablet } = useDeviceType()
const tempModelValue = ref<RangeTuple>([...props.modelValue])
const route = useRoute()

const min = toRef(props.minValue)
const max = toRef(props.maxValue)

watch(
  () => props.modelValue,
  (newVal) => {
    tempModelValue.value = newVal
  },
)

const isActive = ref<boolean>(
  props.modelValue[0] !== min.value || props.modelValue[1] !== max.value,
)

const hasActiveState = ref<boolean>(false)

const roundDownValue = (num = 0) => Math.floor(num / 100)
const roundUpValue = (num = 0) => Math.ceil(num / 100)

const fromValue = computed(() => roundDownValue(tempModelValue.value[0]))
const toValue = computed(() => roundUpValue(tempModelValue.value[1]))

const hasPriceRange = computed(() => min.value !== max.value)

const hasValueChanged = computed(
  () => props.modelValue[0] !== min.value || props.modelValue[1] !== max.value,
)

const handleRangeChange = ([from, to]: [number, number]) => {
  if (
    !showNewDesktopPlpQuickFilters.value ||
    (showNewDesktopPlpQuickFilters.value && isMobileOrTablet.value)
  ) {
    emit('update:modelValue', [
      roundDownValue(from) * 100,
      roundUpValue(to) * 100,
    ])
  } else {
    tempModelValue.value = [roundDownValue(from) * 100, roundUpValue(to) * 100]
  }
}

const handleInputChange = (newFromValue?: number, newToValue?: number) => {
  let from = newFromValue ? newFromValue * 100 : tempModelValue.value[0]
  let to = newToValue ? newToValue * 100 : tempModelValue.value[1]

  from = from ? from : props.modelValue[0]
  to = to ? to : props.modelValue[1]

  if (from > to) {
    const temp = from
    from = to
    to = temp
  }

  from = Math.min(Math.max(from, min.value), max.value)
  to = Math.min(Math.max(to, min.value), max.value)

  tempModelValue.value = [from, to]
  if (
    !showNewDesktopPlpQuickFilters.value ||
    (showNewDesktopPlpQuickFilters.value && isMobileOrTablet.value)
  ) {
    emit('update:modelValue', tempModelValue.value)
  }
}

const handleFromInputChange = useDebounceFn(
  (from: number) => handleInputChange(from),
  INPUT_CHANGE_DELAY,
)

const handleToInputChange = useDebounceFn(
  (to: number) => handleInputChange(undefined, to),
  INPUT_CHANGE_DELAY,
)

const handleReset = () => {
  tempModelValue.value = [min.value, max.value]
  nextTick(() => emit('reset'))
}

const handleResetToInitialState = () => {
  tempModelValue.value = [...props.modelValue]
  handleRangeChange([tempModelValue.value[0], tempModelValue.value[1]])
}

const handleApply = () => {
  emit('update:modelValue', tempModelValue.value)
  emit('apply')
}

watch(
  () => [props.modelValue[0], props.modelValue[1]],
  ([newMin, newMax]) => {
    const hasCustomPriceFilter = !!(
      route.query.minPrice && route.query.maxPrice
    )
    isActive.value = newMin !== min.value || newMax !== max.value
    hasActiveState.value =
      showNewDesktopPlpQuickFilters &&
      !isMobileOrTablet.value &&
      isActive.value &&
      hasCustomPriceFilter
  },
  { immediate: true },
)
</script>
